// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Slider, Table, TableBody, TableCell, TableHead, TableRow, Tooltip as MuiTooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, LineChart,
  Pie, PieChart, PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer,
  Scatter, ScatterChart, ReferenceLine, Tooltip, XAxis, YAxis,
} from 'recharts';
import { CHART_COLORS_LIGHT as CHART_COLORS } from 'Utils/chartColors';
import stylesDef from './styles';
import type { ChatChartSpec } from './chatbotTypes';

const CHART_HEIGHT = 200;
// Shown in place of a chart when there's nothing to plot (empty data, or an all-NaN histogram).
const emptyChartSx = { p: 2, textAlign: 'center' as const, color: 'text.secondary', fontSize: '0.8rem' };

// A record row from the chart's `data` (aggregated attributes, never raw geometry). `_keys` (when the
// Lambda joined it) is this row's feature keys, index-aligned — used for click-to-highlight with no
// category string re-derivation.
type Row = Record<string, string | number | string[] | undefined> & { _keys?: string[] };

// Bin numeric values into ~evenly spaced buckets for a histogram (the SQL returned raw values in one
// column; recharts has no built-in binning). Returns [{ bin: "lo–hi", count }].
const toHistogram = (values: number[], binCount = 12): { bin: string; count: number }[] => {
  const nums = values.filter((value) => Number.isFinite(value));
  if (!nums.length) return [];
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  if (min === max) return [{ bin: String(min), count: nums.length }];
  const width = (max - min) / binCount;
  const bins = Array.from({ length: binCount }, (_unused, index) => ({ lo: min + index * width, hi: min + (index + 1) * width, count: 0 }));
  nums.forEach((value) => {
    const index = Math.min(binCount - 1, Math.floor((value - min) / width));
    bins[index].count += 1;
  });
  return bins.map((bucket) => ({ bin: `${bucket.lo.toFixed(1)}–${bucket.hi.toFixed(1)}`, count: bucket.count }));
};

interface ChatChartProps {
  chart: ChatChartSpec;
  // Fired when the user clicks a category on a highlightable chart, with a GeoJSON FeatureCollection to
  // show on the map. `target` (the layer + the clicked features' join keys) lets the parent highlight
  // the ALREADY-DISPLAYED features in place (correct height); without it the parent draws the geojson.
  onHighlight: (
    label: string,
    geojson: GeoJSON.FeatureCollection,
    color?: string,
    target?: { layer: string; keyField?: string; keyValues?: string[] },
  ) => void;
  // These three are OPTIONAL callbacks whose ABSENCE is meaningful — the component keys behavior off whether
  // the parent passed them (`clickable && onHoverHighlight`; `onFrameChange` in an effect dependency). A no-op
  // default would change that behavior, and defaultProps is deprecated for function components, so no default.
  /* eslint-disable react/require-default-props */
  // Fired on HOVER over a category (same args as onHighlight) — a transient preview highlight the
  // parent shows WITHOUT the click's 5s lock. Paired with onClearHighlight (fired when the cursor
  // leaves the chart). Optional: a chart with neither still works as click-only.
  onHoverHighlight?: (
    label: string,
    geojson: GeoJSON.FeatureCollection,
    color?: string,
    target?: { layer: string; keyField?: string; keyValues?: string[] },
  ) => void;
  onClearHighlight?: () => void;
  // F13: for a parametric (slider) chart, reports the current slider stop so the parent can pin the
  // frame the user settled on (pin captures ONLY the current value, not the whole sweep).
  onFrameChange?: (index: number) => void;
  /* eslint-enable react/require-default-props */
}

/**
 * Render one interactive chart Khora produced (make_chart) with recharts, plus a data-table and a
 * "how it's derived" popup (both plain escaped React text — never dangerouslySetInnerHTML). Clicking
 * a category on a bar/pie/donut highlights the matching features on the map when the chart carries
 * their geometry (see onHighlight). The chart plots aggregated rows the Lambda already computed.
 */
const ChatChart: React.FC<ChatChartProps> = ({
  chart, onHighlight, onHoverHighlight, onClearHighlight, onFrameChange,
}) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const [openTable, setOpenTable] = useState(false);
  const [openInfo, setOpenInfo] = useState(false);
  // Index of the clicked bar/slice so the selected one stays outlined (recharts has no persistent
  // selection of its own); a fresh chart is a fresh component, so this resets per chart.
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  // True when the CURRENT map highlight came from a hover (not a click). Gates chart-leave so a hover
  // preview clears on leave but a click's 5s highlight is left to run out — the click behavior is
  // unchanged. A ref (not state) so reading/setting it never re-renders mid-interaction.
  const lastWasHoverRef = useRef(false);

  const {
    chart_type: chartType, data, x_field: xField, y_field: yField, series_field: seriesField,
    x_label: xLabel, y_label: yLabel, title, explanation, table, highlight, param, frames,
    subtitle, reference_lines: referenceLines, emphasis,
  } = chart;
  // F13 parametric slider: the chart carries a precomputed frame per parameter value. The slider picks
  // a frame; the chart redraws from it in place (no re-fetch). Non-parametric charts plot `data`.
  const isParametric = !!(param && frames && frames.length);
  const [sliderIndex, setSliderIndex] = useState(param?.default_index ?? 0);
  const clampedIndex = isParametric ? Math.min(sliderIndex, frames.length - 1) : 0;
  const activeRows = isParametric ? (frames[clampedIndex] ?? data) : data;
  const rows = (Array.isArray(activeRows) ? activeRows : []) as Row[]; // guard: a restored/malformed chart may lack an array
  // Report the current slider stop so the parent pins the frame the user settled on.
  useEffect(() => { if (isParametric) onFrameChange?.(clampedIndex); }, [isParametric, clampedIndex, onFrameChange]);

  // Strip the grouping commas the SQL/JSON round-trip may leave on a numeric string ("1,234" → 1234).
  // Returns NaN for a NON-EMPTY non-numeric string (e.g. "n/a"), which the histogram's Number.isFinite
  // filter then drops. Note: null/undefined/empty coerce to 0 (finite) — a genuinely missing value bins at
  // 0 rather than being dropped; acceptable here since the SQL for a histogram/axis selects a numeric column.
  const parseLoose = (value: unknown): number => Number(String(value ?? '').replace(/,/g, ''));
  // Coerce for RENDERING an axis — recharts draws 0-height bars / a broken axis on a NaN. The click path
  // reads the ORIGINAL rows (category + _keys), so this affects only what's plotted.
  const toNum = (value: unknown): number => {
    const num = parseLoose(value);
    return Number.isFinite(num) ? num : 0;
  };
  // Multi-series: series_field splits the data into several series. Pivot long→wide so recharts draws one
  // line/area/bar per series value (previously only 'composed' consumed series_field, so a multi-series
  // line/area/bar silently rendered a single series).
  const seriesValues = seriesField ? Array.from(new Set(rows.map((row) => String(row[seriesField])))) : [];
  const isMultiSeries = !!seriesField && seriesValues.length > 1 && ['line', 'area', 'bar'].includes(chartType);
  // Which axes are numeric for THIS type. y is always a measure; scatter plots x too; composed's line rides
  // seriesField as a second measure; category charts keep x as the label. Every listed field gets
  // toNum-coerced so a "1,234" string isn't dropped to a 0-height bar / broken axis.
  const numericAxisFields: string[] = [];
  if (yField) numericAxisFields.push(yField);
  if (chartType === 'scatter' && xField) numericAxisFields.push(xField);
  if (chartType === 'composed' && seriesField) numericAxisFields.push(seriesField);
  const chartRows: Row[] = isMultiSeries
    ? Array.from(rows.reduce((byX, row) => {
      const xValue = String(row[xField]);
      const pivoted = byX.get(xValue) ?? ({ [xField]: row[xField] } as Row);
      pivoted[String(row[seriesField as string])] = toNum(row[yField as string]);
      byX.set(xValue, pivoted);
      return byX;
    }, new Map<string, Row>()).values())
    : rows.map((row) => {
      const coerced: Row = { ...row };
      numericAxisFields.forEach((field) => { coerced[field] = toNum(row[field]); });
      return coerced;
    });

  // Clickable only for a SINGLE-series categorical chart carrying its layer + join key. Multi-series is
  // excluded — its pivoted rows don't align 1:1 with the underlying feature rows, so a click can't map.
  const clickable = !!(highlight?.layer && highlight?.key_field) && !isMultiSeries
    && ['bar', 'pie', 'donut', 'scatter'].includes(chartType);

  // ---- Berinato pass + shared canonicalization ---- //
  // `canon` normalizes a category so numeric drift ('30'/'30.0') matches consistently — used by BOTH the
  // map-highlight lookup and the emphasis coloring (previously it was trapped inside the highlight resolver).
  const canon = (candidate: string): string => { const num = Number(candidate); return Number.isFinite(num) ? String(num) : candidate; };
  const emphasisSet = new Set((emphasis ?? []).map((value) => canon(String(value))));
  const mutedFill = theme.palette.grey[400];
  // Apply emphasis ONLY when a category/series actually matches — else a stray emphasis value would mute
  // EVERY bar (worse than none), so fall back to the normal palette.
  const emphasisMatchesCategory = emphasisSet.size > 0 && chartRows.some((row) => emphasisSet.has(canon(String(row[xField]))));
  const emphasisMatchesSeries = emphasisSet.size > 0 && seriesValues.some((series) => emphasisSet.has(canon(String(series))));
  const barCellFill = (index: number, categoryValue: unknown): string => {
    if (!emphasisMatchesCategory) return CHART_COLORS[index % CHART_COLORS.length];
    return emphasisSet.has(canon(String(categoryValue))) ? CHART_COLORS[0] : mutedFill;
  };
  const seriesFill = (series: string, seriesColor: string): string => {
    if (!emphasisMatchesSeries) return seriesColor;
    return emphasisSet.has(canon(String(series))) ? CHART_COLORS[0] : mutedFill;
  };
  // The color the mark ACTUALLY renders, so the map highlight sent on click/hover matches the bar/slice
  // (emphasis otherwise recolors bars while the highlight kept the old palette color — a visible mismatch).
  const displayedColor = (index: number): string => (
    chartType === 'scatter' ? CHART_COLORS[0] : barCellFill(index, chartRows[index]?.[xField]));
  // High-contrast selection outline: white reads poorly on a muted grey bar, so use a dark stroke there.
  const activeStrokeFor = (fill: string): string => (fill === mutedFill ? theme.palette.text.primary : '#fff');
  // Threshold/target lines. ifOverflow=extendDomain so a line beyond the data range still renders (a target
  // above the tallest bar); x-lines only where the x-axis is numeric (scatter). Rendered AFTER the series so
  // they paint on top of the bars/areas, not under them.
  const renderReferenceLines = (allowX: boolean): React.ReactElement[] => (referenceLines ?? [])
    .filter((line) => allowX || line.axis !== 'x')
    .map((line) => (
      <ReferenceLine
        key={`ref-${line.axis ?? 'y'}-${line.value}-${line.label}`}
        x={line.axis === 'x' ? line.value : undefined}
        y={line.axis === 'x' ? undefined : line.value}
        ifOverflow="extendDomain"
        stroke={theme.palette.text.secondary}
        strokeDasharray="5 4"
        label={{
          value: line.label, position: 'insideTopRight', fontSize: 9, fill: theme.palette.text.secondary,
        }}
      />
    ));

  // Resolve the map-highlight target for a bar/slice (the layer + join key + the category's feature
  // keys), or null if this category can't be highlighted. Shared by the CLICK and HOVER paths so both
  // select the exact same features — only the persistence differs (click = 5s, hover = while hovered).
  const resolveHighlightTarget = (index: number): { label: string; target: { layer: string; keyField: string; keyValues: string[] } } | null => {
    const row = rows[index];
    if (!clickable || !row || !highlight?.layer || !highlight?.key_field) return null;
    const value = row[xField];
    if (value == null) return null;
    const label = String(value);
    // Precedence: (1) server-joined per-row keys — index-aligned, NO string matching, so floats / unicode
    // CASE bands / whitespace can't drift; (2) legacy features_by_category (canon-matched) for charts
    // persisted before _keys; (3) the clicked value itself, but ONLY when there's no mapping at all.
    const byCategory = highlight.features_by_category;
    let keys: string[] | undefined;
    if (row._keys && row._keys.length) keys = row._keys;
    else if (byCategory) {
      keys = byCategory[label];
      if (!keys || !keys.length) {
        const hit = Object.keys(byCategory).find((candidate) => canon(candidate) === canon(label));
        keys = hit ? byCategory[hit] : undefined;
      }
    }
    const hasMapping = row._keys !== undefined || !!byCategory;
    if ((!keys || !keys.length) && hasMapping) {
      // A per-category mapping exists but this category has no keys (an "Other" bucket, or a mismatch) —
      // do NOT fall back to [label], which would highlight WHERE key_field IN ('the category') and match
      // nothing. Skip so a wrong (empty) highlight never appears.
      return null;
    }
    const keyValues = keys && keys.length ? keys : [label];
    return { label: `${title}: ${label}`, target: { layer: highlight.layer, keyField: highlight.key_field, keyValues } };
  };
  const emptyFc: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
  const onCategory = (index: number, color: string): void => {
    const resolved = resolveHighlightTarget(index);
    if (resolved) onHighlight(resolved.label, emptyFc, color, resolved.target);
  };
  // recharts onClick passes (datum, index); the index into the aligned rows is the robust lookup. The
  // clicked bar/slice's own color is sent so the map highlight matches it.
  const onIndex = clickable
    ? (_datum: unknown, index: number): void => {
      lastWasHoverRef.current = false; // a click OWNS the highlight (5s); chart-leave must NOT clear it
      setActiveIndex(index);
      onCategory(index, displayedColor(index));
    }
    : undefined;
  // HOVER: preview the bar/slice's features on the map WHILE the cursor is over it (no 5s lock). Same
  // features as a click; cleared when the cursor leaves the chart (unless a click is holding it).
  const onHoverIndex = clickable && onHoverHighlight
    ? (_datum: unknown, index: number): void => {
      const resolved = resolveHighlightTarget(index);
      if (!resolved) return;
      lastWasHoverRef.current = true;
      onHoverHighlight(resolved.label, emptyFc, displayedColor(index), resolved.target);
    }
    : undefined;
  // MULTI-SERIES highlight: when the SERIES are the features being compared (e.g. 'Pole 25 vs Pole 34'),
  // the mappable entity is the series, not the x-axis. The server keys features_by_category by the series
  // value, so hovering/clicking a series highlights that feature's map features. Only when the server
  // supplied that per-series mapping (a bare {layer, key_field} can't reconstruct which series is which).
  const seriesClickable = isMultiSeries && !!(highlight?.layer && highlight?.key_field && highlight?.features_by_category)
    && ['bar', 'line', 'area'].includes(chartType);
  const resolveSeriesTarget = (seriesValue: string): { label: string; target: { layer: string; keyField: string; keyValues: string[] } } | null => {
    const byCategory = highlight?.features_by_category;
    if (!seriesClickable || !highlight?.layer || !highlight?.key_field || !byCategory) return null;
    const keys = byCategory[seriesValue];
    if (!keys || !keys.length) return null;
    return { label: `${title}: ${seriesValue}`, target: { layer: highlight.layer, keyField: highlight.key_field, keyValues: keys } };
  };
  const onSeriesClick = (seriesValue: string, color: string): void => {
    const resolved = resolveSeriesTarget(seriesValue);
    if (!resolved) return;
    lastWasHoverRef.current = false;
    onHighlight(resolved.label, emptyFc, color, resolved.target);
  };
  const onSeriesHover = (seriesValue: string, color: string): void => {
    if (!onHoverHighlight) return;
    const resolved = resolveSeriesTarget(seriesValue);
    if (!resolved) return;
    lastWasHoverRef.current = true;
    onHoverHighlight(resolved.label, emptyFc, color, resolved.target);
  };
  // Cursor left the chart: clear ONLY a hover preview, never a click's 5s highlight.
  const onChartLeave = (): void => {
    if (lastWasHoverRef.current) {
      lastWasHoverRef.current = false;
      onClearHighlight?.();
    }
  };

  const cursor = clickable ? 'pointer' : 'default';
  const axisTick = { fontSize: 10, fill: theme.palette.text.secondary };
  const grid = <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />;
  const tip = <Tooltip contentStyle={{ fontSize: 11 }} />;
  // Bar-based charts: kill recharts' default translucent cursor band (a gray <Rectangle> drawn behind
  // the whole hovered category) — the bar itself is emphasized instead (activeBar on hover, Cell
  // outline on click), so the highlight is the bar, not the background.
  const barTip = <Tooltip cursor={false} contentStyle={{ fontSize: 11 }} />;
  // White outline for the active bar (hover) and the clicked bar/slice (persists until another click).
  const activeBar = { stroke: '#fff', strokeWidth: 1.5 };
  const isActive = (index: number): boolean => index === activeIndex;

  const renderBody = (): React.ReactElement => {
    switch (chartType) {
    case 'line':
      return (
        <LineChart data={chartRows}>
          {grid}
          <XAxis dataKey={xField} tick={axisTick} />
          <YAxis tick={axisTick} />
          {tip}
          {isMultiSeries
            ? seriesValues.map((series, index) => (
              <Line key={series} type="monotone" dataKey={series} stroke={CHART_COLORS[index % CHART_COLORS.length]} dot={false} />
            ))
            : <Line type="monotone" dataKey={yField} stroke={CHART_COLORS[0]} dot={false} />}
          {renderReferenceLines(false)}
          {isMultiSeries && <Legend wrapperStyle={{ fontSize: 10 }} />}
        </LineChart>
      );
    case 'area':
      return (
        <AreaChart data={chartRows}>
          {grid}
          <XAxis dataKey={xField} tick={axisTick} />
          <YAxis tick={axisTick} />
          {tip}
          {isMultiSeries
            ? seriesValues.map((series, index) => (
              <Area key={series} type="monotone" dataKey={series} stroke={CHART_COLORS[index % CHART_COLORS.length]} fill={CHART_COLORS[index % CHART_COLORS.length]} fillOpacity={0.3} />
            ))
            : <Area type="monotone" dataKey={yField} stroke={CHART_COLORS[0]} fill={CHART_COLORS[0]} fillOpacity={0.3} />}
          {renderReferenceLines(false)}
          {isMultiSeries && <Legend wrapperStyle={{ fontSize: 10 }} />}
        </AreaChart>
      );
    case 'scatter':
      return (
        <ScatterChart>
          {grid}
          <XAxis type="number" dataKey={xField} name={xLabel || xField} tick={axisTick} />
          <YAxis type="number" dataKey={yField} name={yLabel || yField} tick={axisTick} />
          <Tooltip cursor={{ strokeDasharray: '3 3' }} contentStyle={{ fontSize: 11 }} />
          <Scatter data={chartRows} fill={CHART_COLORS[0]} onClick={onIndex} onMouseEnter={onHoverIndex} cursor={cursor} />
          {renderReferenceLines(true)}
        </ScatterChart>
      );
    case 'pie':
    case 'donut':
      return (
        <PieChart>
          <Pie
            data={chartRows}
            dataKey={yField as string}
            nameKey={xField}
            innerRadius={chartType === 'donut' ? 38 : 0}
            outerRadius={68}
            onClick={onIndex}
            onMouseEnter={onHoverIndex}
          >
            {chartRows.map((row, index) => (
              // eslint-disable-next-line react/no-array-index-key -- key is `<category>-<index>`; the index disambiguates rows that share a category, and recharts' data is static per render
              <Cell key={`${String(row[xField])}-${index}`} fill={barCellFill(index, row[xField])} cursor={cursor} stroke={isActive(index) ? activeStrokeFor(barCellFill(index, row[xField])) : undefined} strokeWidth={isActive(index) ? 2 : undefined} />
            ))}
          </Pie>
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {tip}
        </PieChart>
      );
    case 'histogram': {
      const bins = toHistogram(rows.map((row) => parseLoose(row[xField])));
      if (!bins.length) return <Typography sx={emptyChartSx}>No numeric values to bin.</Typography>;
      return (
        <BarChart data={bins}>
          {grid}
          <XAxis dataKey="bin" tick={{ ...axisTick, fontSize: 8 }} interval={1} />
          <YAxis tick={axisTick} />
          {barTip}
          <Bar dataKey="count" fill={CHART_COLORS[0]} activeBar={activeBar} />
          {renderReferenceLines(false)}
        </BarChart>
      );
    }
    case 'radar':
      return (
        <RadarChart data={chartRows}>
          <PolarGrid />
          <PolarAngleAxis dataKey={xField} tick={axisTick} />
          <PolarRadiusAxis tick={axisTick} />
          {tip}
          <Radar dataKey={yField as string} stroke={CHART_COLORS[0]} fill={CHART_COLORS[0]} fillOpacity={0.4} />
        </RadarChart>
      );
    case 'composed':
      return (
        <ComposedChart data={chartRows}>
          {grid}
          <XAxis dataKey={xField} tick={axisTick} />
          <YAxis tick={axisTick} />
          {barTip}
          <Legend wrapperStyle={{ fontSize: 10 }} />
          <Bar dataKey={yField as string} fill={CHART_COLORS[0]} />
          {seriesField && <Line type="monotone" dataKey={seriesField} stroke={CHART_COLORS[1]} dot={false} />}
          {renderReferenceLines(false)}
        </ComposedChart>
      );
    case 'bar':
    default:
      if (isMultiSeries) {
        return (
          <BarChart data={chartRows}>
            {grid}
            <XAxis dataKey={xField} tick={axisTick} />
            <YAxis tick={axisTick} />
            {barTip}
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {seriesValues.map((series, index) => {
              // The DISPLAYED color (emphasis recolors the accented series and mutes the rest); the map
              // highlight sent on click/hover uses the same color so bar and highlight stay in sync.
              const displayed = seriesFill(series, CHART_COLORS[index % CHART_COLORS.length]);
              return (
                <Bar
                  key={series}
                  dataKey={series}
                  fill={displayed}
                  activeBar={seriesClickable ? activeBar : undefined}
                  cursor={seriesClickable ? 'pointer' : 'default'}
                  onClick={seriesClickable ? () => onSeriesClick(series, displayed) : undefined}
                  onMouseEnter={seriesClickable ? () => onSeriesHover(series, displayed) : undefined}
                />
              );
            })}
            {renderReferenceLines(false)}
          </BarChart>
        );
      }
      return (
        <BarChart data={chartRows}>
          {grid}
          <XAxis dataKey={xField} tick={axisTick} />
          <YAxis tick={axisTick} />
          {barTip}
          <Bar dataKey={yField as string} onClick={onIndex} onMouseEnter={onHoverIndex} activeBar={activeBar}>
            {chartRows.map((row, index) => (
              // eslint-disable-next-line react/no-array-index-key -- key is `<category>-<index>`; the index disambiguates rows that share a category, and recharts' data is static per render
              <Cell key={`${String(row[xField])}-${index}`} fill={barCellFill(index, row[xField])} cursor={cursor} stroke={isActive(index) ? activeStrokeFor(barCellFill(index, row[xField])) : undefined} strokeWidth={isActive(index) ? 2 : undefined} />
            ))}
          </Bar>
          {renderReferenceLines(false)}
        </BarChart>
      );
    }
  };

  const tableColumns = table?.columns ?? [];
  // Show ALL rows — never cap the data view (the card scrolls). The chart summarizes; the table is the
  // full underlying data, so we don't drop rows for display size.
  const tableRows = table?.rows ?? [];

  return (
    <Box sx={styles.chartCard}>
      <Box sx={styles.chartHeader}>
        <Typography sx={styles.chartTitle}>{title}</Typography>
        <Box sx={styles.chartTools}>
          <MuiTooltip title="Show the data behind this chart">
            <IconButton size="small" sx={styles.chartToolBtn} onClick={() => setOpenTable((open) => !open)}>
              <TableChartOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </MuiTooltip>
          <MuiTooltip title="How this chart is derived">
            <IconButton size="small" sx={styles.chartToolBtn} onClick={() => setOpenInfo((open) => !open)}>
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </MuiTooltip>
        </Box>
      </Box>

      {subtitle && (
        <Typography sx={{ px: 1.5, mt: -0.5, mb: 0.5, fontSize: '0.72rem', color: 'text.secondary' }}>
          {subtitle}
        </Typography>
      )}

      <Box sx={styles.chartContainer} onMouseLeave={onChartLeave}>
        {chartRows.length === 0
          ? <Typography sx={emptyChartSx}>No data to chart.</Typography>
          : <ResponsiveContainer width="100%" height={CHART_HEIGHT}>{renderBody()}</ResponsiveContainer>}
      </Box>

      {isParametric && param && (
        <Box sx={{ px: 1.5, pb: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{param.name}</Typography>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: 'primary.main', fontVariantNumeric: 'tabular-nums' }}>
              {`${param.values[clampedIndex]}${param.unit ? ` ${param.unit}` : ''}`}
            </Typography>
          </Box>
          <Slider
            size="small"
            min={0}
            max={param.values.length - 1}
            step={1}
            value={clampedIndex}
            onChange={(_event, value) => setSliderIndex(Array.isArray(value) ? value[0] : value)}
            valueLabelDisplay="off"
            aria-label={`${param.name} value`}
          />
        </Box>
      )}

      {(clickable || seriesClickable) && <Typography sx={styles.chartHint}>Hover or click a bar/slice to highlight those features on the map.</Typography>}
      {isParametric && <Typography sx={styles.chartHint}>Drag the slider to recompute; pin to keep the current value.</Typography>}

      {openInfo && (
        <Box sx={styles.methodPanel}>
          <Typography sx={styles.methodLine}>{explanation}</Typography>
          {clickable && <Typography sx={styles.methodLine}>Click a datapoint to highlight the matching feature(s) on the map.</Typography>}
        </Box>
      )}

      {openTable && tableColumns.length > 0 && (
        <Box sx={styles.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>{tableColumns.map((column) => <TableCell key={column} sx={styles.thCell}>{column}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {tableRows.map((row) => (
                <TableRow key={tableColumns.map((column) => String(row[column])).join('|')}>
                  {tableColumns.map((column) => <TableCell key={column} sx={styles.tdCell}>{String(row[column] ?? '')}</TableCell>)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default ChatChart;
